/**
 * Dashboard Manager Component
 *
 * Modal for managing multiple dashboards:
 * - View all dashboards
 * - Create new dashboards
 * - Rename dashboards
 * - Duplicate dashboards
 * - Delete dashboards
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState } from 'react';
import {
  X,
  Plus,
  Copy,
  Trash2,
  Edit2,
  Check,
  LayoutGrid,
  Clock,
  Star,
  BookTemplate,
} from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useDashboard } from '../DashboardContext';
import { DashboardLayout } from '../types';
import TemplateSelector from './TemplateSelector';

interface DashboardManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DashboardManager: React.FC<DashboardManagerProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const {
    state,
    loadLayout,
    createLayout,
    deleteLayout,
    duplicateLayout,
    saveLayout,
  } = useDashboard();

  const { currentLayout, availableLayouts } = state;
  const [newDashboardName, setNewDashboardName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (newDashboardName.trim()) {
      createLayout(newDashboardName.trim());
      setNewDashboardName('');
      setShowCreateForm(false);
    }
  };

  const handleDuplicate = (layout: DashboardLayout) => {
    const newName = `${layout.name} ${t('manager.modules.wmsDashboard.manager.copySuffix', {}, '(kopi)')}`;
    duplicateLayout(layout.id, newName);
  };

  const handleDelete = (layoutId: string) => {
    if (availableLayouts.length > 1) {
      deleteLayout(layoutId);
    }
  };

  const handleStartRename = (layout: DashboardLayout) => {
    setEditingId(layout.id);
    setEditingName(layout.name);
  };

  const handleSaveRename = () => {
    if (editingId && editingName.trim()) {
      const layout = availableLayouts.find(l => l.id === editingId);
      if (layout) {
        saveLayout({ ...layout, name: editingName.trim() });
      }
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleSelect = (layoutId: string) => {
    loadLayout(layoutId);
    onClose();
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return t('manager.modules.wmsDashboard.manager.unknownDate', {}, 'Ukjent dato');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div
        className="bg-gray-900 rounded-xl shadow-2xl border border-gray-700 w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <LayoutGrid className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">{t('manager.modules.wmsDashboard.manager.title', {}, 'Administrer Dashboards')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Dashboard List */}
          <div className="space-y-3">
            {availableLayouts.map((layout) => {
              const isActive = layout.id === currentLayout.id;
              const isEditing = editingId === layout.id;

              return (
                <div
                  key={layout.id}
                  className={`
                    p-4 rounded-lg border-2 transition-all
                    ${isActive
                      ? 'bg-blue-900/30 border-blue-500'
                      : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRename();
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                          <button
                            onClick={handleSaveRename}
                            className="p-2 bg-green-600 hover:bg-green-500 rounded-lg text-white transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium text-white truncate">
                              {layout.name}
                            </h3>
                            {layout.isDefault && (
                              <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                            )}
                            {isActive && (
                              <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                                {t('manager.modules.wmsDashboard.manager.active', {}, 'Aktiv')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span>{t('manager.modules.wmsDashboard.toolbar.widgetCount', { '0': layout.widgets.length }, `${layout.widgets.length} widgets`)}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(layout.updatedAt)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    {!isEditing && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!isActive && (
                          <button
                            onClick={() => handleSelect(layout.id)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
                          >
                            {t('common.buttons.select', {}, 'Velg')}
                          </button>
                        )}
                        <button
                          onClick={() => handleStartRename(layout)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                          title={t('manager.modules.wmsDashboard.manager.rename', {}, 'Gi nytt navn')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(layout)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                          title={t('manager.modules.wmsDashboard.manager.duplicate', {}, 'Dupliser')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        {availableLayouts.length > 1 && !layout.isDefault && (
                          <button
                            onClick={() => handleDelete(layout.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title={t('common.buttons.delete', {}, 'Slett')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Create New Dashboard */}
          <div className="mt-4">
            {showCreateForm ? (
              <div className="p-4 bg-gray-800 rounded-lg border-2 border-dashed border-gray-600">
                <label className="block text-sm text-gray-400 mb-2">
                  {t('manager.modules.wmsDashboard.manager.newDashboardName', {}, 'Navn på nytt dashboard')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newDashboardName}
                    onChange={(e) => setNewDashboardName(e.target.value)}
                    placeholder={t('manager.modules.wmsDashboard.manager.newDashboardPlaceholder', {}, "F.eks. 'Lager Nord' eller 'Plukk Dashboard'")}
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreate();
                      if (e.key === 'Escape') {
                        setShowCreateForm(false);
                        setNewDashboardName('');
                      }
                    }}
                  />
                  <button
                    onClick={handleCreate}
                    disabled={!newDashboardName.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {t('common.buttons.create', {}, 'Opprett')}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewDashboardName('');
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    {t('common.buttons.cancel', {}, 'Avbryt')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="flex-1 p-4 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-750 border-2 border-dashed border-gray-600 hover:border-blue-500 rounded-lg text-gray-400 hover:text-blue-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('manager.modules.wmsDashboard.manager.createEmpty', {}, 'Opprett tomt dashboard')}</span>
                </button>
                <button
                  onClick={() => setShowTemplateSelector(true)}
                  className="flex-1 p-4 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-750 border-2 border-dashed border-gray-600 hover:border-violet-500 rounded-lg text-gray-400 hover:text-violet-400 transition-all"
                >
                  <BookTemplate className="w-5 h-5" />
                  <span>{t('manager.modules.wmsDashboard.manager.createFromTemplate', {}, 'Opprett fra mal')}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <p className="text-sm text-gray-500 text-center">
            {t('manager.modules.wmsDashboard.manager.totalCount', { '0': availableLayouts.length }, `${availableLayouts.length} dashboard${availableLayouts.length !== 1 ? 's' : ''} totalt`)}
          </p>
        </div>
      </div>

      {/* Template Selector Overlay */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => {
          setShowTemplateSelector(false);
          onClose(); // Close manager too when template is selected
        }}
      />
    </div>
  );
};

export default DashboardManager;
